import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Ruler, ImageIcon, ExternalLink, Loader2, Pin, PinOff } from "lucide-react";
import { CATEGORIES } from "@/data/portfolioData";
import Lightbox from "@/components/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER_IDS = {
  All: "gallery-filter-all",
  "Luxury Estates": "gallery-filter-luxury-estates",
  "Modern Architecture": "gallery-filter-modern-architecture",
  "Twilight & Drone": "gallery-filter-twilight-drone",
  "Interior Staging": "gallery-filter-interior-staging",
};

const Gallery = ({ albums }) => {
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [remoteAlbums, setRemoteAlbums] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [curateMode, setCurateMode] = useState(false);

  const loadAlbums = useCallback(() => {
    return axios
      .get(`${API}/albums`)
      .then(({ data }) =>
        setRemoteAlbums(
          data.map((a) => ({
            id: a.key,
            title: a.title,
            category: "My Photos",
            location: "",
            price: "",
            sqft: "",
            photos: null,
            cover: a.cover,
            photoCount: a.photoCount,
            googleAlbumUrl: a.sourceUrl,
            remote: true,
            pinned: a.pinned,
          }))
        )
      )
      .catch(() => toast.error("Couldn't load your Google albums right now."));
  }, []);

  useEffect(() => {
    loadAlbums();
  }, [loadAlbums]);

  const allAlbums = [...remoteAlbums, ...albums];
  const visible = filter === "All" ? allAlbums : allAlbums.filter((a) => a.category === filter);
  const pinnedCount = remoteAlbums.filter((a) => a.pinned).length;

  const togglePin = async (album, e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(`${API}/albums/${album.id}/pin`);
      toast.success(
        data.pinned ? `"${album.title}" pinned to the top.` : `"${album.title}" unpinned.`
      );
      await loadAlbums();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("You've pinned 6 already — unpin one first.");
      } else {
        toast.error("Couldn't update the pin. Try again.");
      }
    }
  };

  const openAlbum = async (album) => {
    if (album.remote) {
      setLoadingId(album.id);
      try {
        const { data } = await axios.get(`${API}/albums/${album.id}`);
        setLightbox({ album: { ...album, photos: data.photos }, index: 0 });
      } catch {
        toast.error("Couldn't load this album's photos — opening it in Google Photos instead.");
        if (album.googleAlbumUrl) window.open(album.googleAlbumUrl, "_blank", "noopener");
      } finally {
        setLoadingId(null);
      }
      return;
    }
    if (album.photos && album.photos.length > 0) {
      setLightbox({ album, index: 0 });
    } else if (album.googleAlbumUrl) {
      window.open(album.googleAlbumUrl, "_blank", "noopener");
    }
  };

  return (
    <section id="albums" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
      >
        <p className="eyebrow-label mb-4">01 · Selected Work</p>
        <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-zinc-50 sm:text-3xl lg:text-4xl">
          Property albums, shot like editorials
        </h2>
        <p className="mt-4 max-w-xl text-base text-zinc-400">
          Every listing gets a curated album your clients can flip through — click any property to open the full gallery.
        </p>
      </motion.div>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            data-testid={FILTER_IDS[c] || `gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => setFilter(c)}
            className={`rounded-full border px-5 py-2 text-xs font-semibold tracking-wide transition-all duration-300 ${
              filter === c
                ? "border-[#D4AF37] bg-[#D4AF37] text-zinc-950"
                : "border-white/15 text-zinc-400 hover:border-[#D4AF37]/50 hover:text-[#E2C275]"
            }`}
          >
            {c}
          </button>
        ))}
        <button
          data-testid="gallery-curate-toggle"
          onClick={() => {
            setCurateMode((v) => {
              if (!v) toast.info("Curate mode on — tap the pin on up to 6 albums to feature them at the top.");
              return !v;
            });
          }}
          className={`ml-auto flex items-center gap-2 rounded-full border px-5 py-2 text-xs font-semibold tracking-wide transition-all duration-300 ${
            curateMode
              ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#E2C275]"
              : "border-white/15 text-zinc-500 hover:border-[#D4AF37]/50 hover:text-[#E2C275]"
          }`}
        >
          <Pin size={13} />
          {curateMode ? `Curating · ${pinnedCount}/6 pinned` : "Curate"}
        </button>
      </div>

      <motion.div layout className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {visible.map((album) => {
            const coverSrc = album.photos?.length ? album.photos[0].src : album.cover;
            const count = album.photos?.length ?? album.photoCount ?? 0;
            return (
              <motion.article
                layout
                key={album.id}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.55 }}
                data-testid="album-card-item"
                className={`luxury-card spotlight-card group cursor-pointer overflow-hidden ${
                  album.pinned ? "!border-[#D4AF37]/50 shadow-[0_0_32px_rgba(212,175,55,0.1)]" : ""
                }`}
                onMouseMove={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                }}
                onClick={() => openAlbum(album)}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  {coverSrc ? (
                    <img
                      src={coverSrc}
                      alt={album.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-[#18181E]">
                      <ImageIcon size={40} className="text-zinc-600" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/85 via-transparent to-transparent" />
                  <div className="absolute left-4 top-4 flex gap-2">
                    <span className="font-tech rounded-full bg-[#0A0A0C]/70 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#E2C275] backdrop-blur-sm">
                      {album.category}
                    </span>
                    {album.pinned && (
                      <span
                        data-testid="album-featured-badge"
                        className="font-tech flex items-center gap-1 rounded-full bg-[#D4AF37] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-950"
                      >
                        <Pin size={10} /> Featured
                      </span>
                    )}
                    {album.googleAlbumUrl && !album.pinned && (
                      <span className="font-tech flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-emerald-400 backdrop-blur-sm">
                        <ExternalLink size={10} /> Google Album
                      </span>
                    )}
                  </div>
                  {curateMode && album.remote && (
                    <button
                      data-testid="album-pin-btn"
                      onClick={(e) => togglePin(album, e)}
                      aria-label={album.pinned ? `Unpin ${album.title}` : `Pin ${album.title}`}
                      className={`absolute right-4 top-4 z-10 rounded-full border p-2.5 backdrop-blur-sm transition-all duration-200 ${
                        album.pinned
                          ? "border-[#D4AF37] bg-[#D4AF37] text-zinc-950"
                          : "border-white/25 bg-[#0A0A0C]/60 text-zinc-200 hover:border-[#D4AF37]/70 hover:text-[#E2C275]"
                      }`}
                    >
                      {album.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                    </button>
                  )}
                  {loadingId === album.id ? (
                    <span className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#0A0A0C]/70 px-4 py-1.5 text-xs font-semibold text-[#E2C275] backdrop-blur-sm">
                      <Loader2 size={13} className="animate-spin" /> Loading…
                    </span>
                  ) : (
                    <button
                      data-testid="album-open-lightbox-btn"
                      className="absolute bottom-4 right-4 rounded-full border border-white/25 bg-[#0A0A0C]/60 px-4 py-1.5 text-xs font-semibold text-zinc-100 opacity-0 backdrop-blur-sm transition-all duration-300 group-hover:opacity-100"
                    >
                      View Album
                    </button>
                  )}
                </div>

                <div className="p-6">
                  <h3 data-testid="album-card-title" className="font-display text-xl font-semibold tracking-tight text-zinc-50">
                    {album.title}
                  </h3>
                  {(album.location || album.sqft) && (
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
                      {album.location && (
                        <span className="flex items-center gap-1.5"><MapPin size={12} className="text-[#D4AF37]" />{album.location}</span>
                      )}
                      {album.sqft && (
                        <span className="flex items-center gap-1.5"><Ruler size={12} className="text-[#D4AF37]" />{album.sqft}</span>
                      )}
                    </div>
                  )}
                  <div className="mt-4 flex items-center justify-between border-t border-white/[0.07] pt-4">
                    <span className="font-tech text-sm font-semibold text-[#E2C275]">{album.price || ""}</span>
                    <span data-testid="album-card-photo-count" className="font-tech text-xs text-zinc-500">
                      {count} PHOTOS
                    </span>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {lightbox && (
        <Lightbox
          album={lightbox.album}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onNavigate={(i) => setLightbox((s) => ({ ...s, index: i }))}
        />
      )}
    </section>
  );
};

export default Gallery;
